// The text model behind the HTML and CSS boxes in the Custom HTML/CSS editor.
//
// A textarea cannot colour its own text or draw a highlight over part of it, so
// the editor paints a backdrop underneath one: the same characters, in the same
// font, with syntax colours and search highlights, sitting behind a textarea
// whose own text is transparent. Everything that decides what that backdrop
// looks like lives here, away from the DOM, so it can be reasoned about and
// tested as plain string work.
//
// The output is deliberately line-by-line. The line is the unit the gutter
// numbers, so the backdrop and the numbers beside it are built from the same
// array and cannot disagree about how many lines there are.
import Prism from 'prismjs';

// Which Prism grammar each of our field languages is written in. Prism's core
// bundle carries markup and css, which is all the block allows.
const GRAMMARS = {
  html: 'markup',
  css: 'css'
};

// Past this many characters, colouring every keystroke costs more than it is
// worth. Search highlighting and line numbers carry on regardless — those are
// the parts an author is actually using at that size.
export const MAX_HIGHLIGHT_LENGTH = 60000;

const WORD_CHAR = /[A-Za-z0-9_$-]/;

const isWordChar = (char) => Boolean(char) && WORD_CHAR.test(char);

/**
 * Every occurrence of `query` in `value`, as `{ start, end }` offsets.
 *
 * Matches never overlap: the scan resumes at the end of the one it just took,
 * which is what "3 of 12" in the find bar has to count to make sense.
 */
export function findMatches(value, query, options = {}) {
  const { caseSensitive = false, wholeWord = false } = options;
  if (!value || !query) return [];

  const haystack = caseSensitive ? value : value.toLowerCase();
  const needle = caseSensitive ? query : query.toLowerCase();
  if (!needle) return [];

  const matches = [];
  let from = 0;
  while (from <= haystack.length) {
    const start = haystack.indexOf(needle, from);
    if (start === -1) break;
    const end = start + needle.length;
    const bounded = !wholeWord
      || (!isWordChar(value[start - 1]) && !isWordChar(value[end]));
    if (bounded) {
      matches.push({ start, end });
      from = end;
    } else {
      // Step by one, not by the whole word: a rejected occurrence can still
      // sit inside a legitimate one further along.
      from = start + 1;
    }
  }
  return matches;
}

/**
 * The token stream, flattened to a list of `{ text, className }` runs that
 * concatenate back to the original source.
 *
 * Prism nests tokens (an attribute value lives inside a tag). The class we
 * keep is the innermost one enclosing each piece of text, which is exactly the
 * element Prism's own HTML output would have put the text in — so the token
 * colours in the stylesheet mean the same thing here.
 */
function flattenTokens(tokens, className, runs) {
  tokens.forEach((token) => {
    if (typeof token === 'string') {
      if (token) runs.push({ text: token, className });
      return;
    }
    const aliases = Array.isArray(token.alias) ? token.alias : (token.alias ? [token.alias] : []);
    const ownClass = ['token', token.type, ...aliases].filter(Boolean).join(' ');
    if (typeof token.content === 'string') {
      if (token.content) runs.push({ text: token.content, className: ownClass });
      return;
    }
    flattenTokens([].concat(token.content), ownClass, runs);
  });
  return runs;
}

function syntaxRuns(value, language) {
  const grammarName = GRAMMARS[language];
  const grammar = grammarName && Prism.languages ? Prism.languages[grammarName] : null;
  if (!grammar) return [{ text: value, className: '' }];
  try {
    return flattenTokens(Prism.tokenize(value, grammar), '', []);
  } catch (err) {
    // A tokenizer that trips on half-typed markup must not take the editor
    // down with it. Plain text still edits fine.
    return [{ text: value, className: '' }];
  }
}

/**
 * The backdrop, as an array of lines, each an array of
 * `{ text, className, hit }` segments.
 *
 * `hit` is 0 for ordinary text, 1 for a search match and 2 for the match the
 * find bar is currently sitting on. Segments are cut wherever a token boundary,
 * a match boundary or a newline falls, so a match spanning three tokens still
 * gets one continuous highlight.
 */
export function buildHighlightedLines(value, options = {}) {
  const {
    language = 'html',
    matches = [],
    activeIndex = -1,
    syntax = true
  } = options;

  const source = typeof value === 'string' ? value : '';
  const runs = syntax && source.length <= MAX_HIGHLIGHT_LENGTH
    ? syntaxRuns(source, language)
    : [{ text: source, className: '' }];

  const lines = [[]];
  const push = (text, className, hit) => {
    if (!text) return;
    lines[lines.length - 1].push({ text, className, hit });
  };

  let offset = 0;
  let matchIndex = 0;

  runs.forEach((run) => {
    const { text } = run;
    let pos = 0;
    while (pos < text.length) {
      const absolute = offset + pos;
      while (matchIndex < matches.length && matches[matchIndex].end <= absolute) {
        matchIndex += 1;
      }
      const match = matches[matchIndex];

      let hit = 0;
      let end = text.length;
      if (match && match.start <= absolute) {
        hit = matchIndex === activeIndex ? 2 : 1;
        end = Math.min(end, match.end - offset);
      } else if (match) {
        end = Math.min(end, match.start - offset);
      }

      const newline = text.indexOf('\n', pos);
      const breaks = newline !== -1 && newline < end;
      if (breaks) end = newline;

      push(text.slice(pos, end), run.className, hit);

      if (breaks) {
        lines.push([]);
        pos = end + 1;
      } else if (end > pos) {
        pos = end;
      } else {
        // Cannot happen with well-formed match offsets; bail rather than spin.
        break;
      }
    }
    offset += text.length;
  });

  return lines;
}

/** The 1-based line and column an offset falls on. */
export function caretPosition(value, offset) {
  const upto = (typeof value === 'string' ? value : '').slice(0, Math.max(0, offset));
  const lineStart = upto.lastIndexOf('\n') + 1;
  return {
    line: upto.split('\n').length,
    column: upto.length - lineStart + 1
  };
}
